import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Badge, Popover, Drawer, List, Avatar, Empty, Typography, Button } from 'antd';
import { MessageOutlined, RocketOutlined, MailOutlined } from '@ant-design/icons';
import api from '../../services/api';

const { Text } = Typography;

function timeAgo(iso) {
  if (!iso) return '';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'agora';
  if (s < 3600) return `${Math.floor(s / 60)}min`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  const d = Math.floor(s / 86400);
  if (d < 30) return `${d}d`;
  return new Date(iso).toLocaleDateString('pt-BR');
}

// Widget flutuante de conversas nao lidas. Fica no header dos layouts
// da comunidade (jogador + dono time). Polling 30s.
export default function ChatInboxWidget() {
  const [data, setData] = useState({ total_unread: 0, conversations: [] });
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/community/me/inbox');
      setData(r.data || { total_unread: 0, conversations: [] });
    } catch { /* silencioso */ }
  }, []);

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, 30000);
    return () => clearInterval(timerRef.current);
  }, [load]);

  const onClickConversation = async (c) => {
    // Marca como lida antes de redirecionar; UI reflete zerado assim que
    // volta pra widget (poll rebusca ou o proximo mount).
    await api.post('/community/me/inbox/read', { kind: c.kind, ref_id: c.ref_id }).catch(() => {});
    setData(d => ({
      conversations: d.conversations.map(x =>
        (x.kind === c.kind && x.ref_id === c.ref_id) ? { ...x, unread: 0 } : x),
      total_unread: Math.max(0, d.total_unread - (c.unread || 0)),
    }));
    setOpen(false);
    window.location.href = c.url;
  };

  // Em mobile usa Drawer (side-panel full-height) — Popover do Ant Design
  // com placement=bottomRight sofre com viewports estreitos: ora corta
  // esquerda, ora nao mostra a arrow no lugar certo. Drawer resolve.
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

  const listContent = (
    <div style={{
      width: isMobile ? '100%' : 420,
      maxHeight: isMobile ? 'calc(100vh - 120px)' : 480,
      overflowY: 'auto',
    }}>
      {data.conversations.length === 0 ? (
        <Empty description="Nenhuma conversa" style={{ padding: 20 }} />
      ) : (
        <List
          size="small"
          dataSource={data.conversations}
          renderItem={c => (
            <List.Item onClick={() => onClickConversation(c)}
              style={{
                cursor: 'pointer', padding: '14px 18px',
                background: c.unread > 0 ? '#f0fdf4' : 'transparent',
              }}>
              <List.Item.Meta
                avatar={
                  <Badge count={c.unread} size="small">
                    <Avatar size={40} icon={c.kind === 'friendly' ? <RocketOutlined /> : <MailOutlined />}
                      style={{ background: c.kind === 'friendly' ? '#faad14' : '#128C7E' }} />
                  </Badge>
                }
                title={
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
                    <span style={{
                      fontSize: 14, fontWeight: c.unread > 0 ? 700 : 500,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flex: 1,
                    }}>
                      {c.other_name || (c.kind === 'friendly' ? 'Amistoso' : 'Convite')}
                    </span>
                    <Text type="secondary" style={{ fontSize: 11, flexShrink: 0 }}>{timeAgo(c.last_at)}</Text>
                  </div>
                }
                description={
                  <Text style={{ fontSize: 13, color: c.unread > 0 ? '#333' : '#8c8c8c' }} ellipsis>
                    {c.last_preview || '—'}
                  </Text>
                }
              />
            </List.Item>
          )}
        />
      )}
    </div>
  );

  const trigger = (
    <Badge count={data.total_unread} size="small" offset={[-2, 4]}>
      <Button type="text" icon={<MessageOutlined style={{ fontSize: 18 }} />}
        onClick={isMobile ? () => setOpen(true) : undefined} />
    </Badge>
  );

  if (isMobile) {
    return (
      <>
        {trigger}
        <Drawer title="Conversas" placement="right" width="100%"
          open={open} onClose={() => setOpen(false)}
          styles={{ body: { padding: 0 } }}>
          {listContent}
        </Drawer>
      </>
    );
  }

  return (
    <Popover content={listContent} trigger="click" open={open} onOpenChange={setOpen}
      placement="bottomRight" title="Conversas"
      overlayInnerStyle={{ padding: 0 }}
      overlayStyle={{ maxWidth: 460 }}>
      {trigger}
    </Popover>
  );
}
